<script setup>
import { computed } from "vue";
import { useGetters } from "vuex-composition-helpers";
import { cleanUpEmployments } from "@/common/employments/cleanUpEmployments";

import RecommendationsTokens from "@/components/RecommendationsTokens/RecommendationsTokens";
import Employment from "./Employment/Employment";
import AboutMe from "./AboutMe";
import Education from "./Education";
import Award from "./Award";
import Certification from "./Certification";
import Course from "./Course";
import Patent from "./Patent";
import Publication from "./Publication";
import Project from "./Project";

const {
  currentCandidate,
  currentProject,
  isLoadingCurrentCandidate,
  isLoadingCurrentCandidateCompanies,
  isRecommendationTokensHidden,
} = useGetters([
  "currentCandidate",
  "currentProject",
  "isLoadingCurrentCandidate",
  "isLoadingCurrentCandidateCompanies",
  "isRecommendationTokensHidden",
]);

const showRecommendationsTokens = computed(() => {
  return !!currentCandidate.value.similarity?.analysis;
});

const recommendationTokensVisible = computed(() => {
  return showRecommendationsTokens.value && !isRecommendationTokensHidden.value;
});

const cleanedUpEmployments = computed(() => {
  return cleanUpEmployments(currentCandidate.value.employments);
});

const sortedAwards = computed(() => {
  const awards = currentCandidate.value.awards || [];
  return sortAlphabetically(awards, "award");
});

const sortedCertifications = computed(() => {
  const certs = currentCandidate.value.certifications || [];
  return sortByYearThenAlphabetically(certs, "time", "certificationName");
});

const sortedCourses = computed(() => {
  const courses = currentCandidate.value.courses || [];
  return sortAlphabetically(courses, "course");
});

const sortedPatents = computed(() => {
  const patents = currentCandidate.value.patents || [];
  return sortAlphabetically(patents, "title");
});

const sortedPublications = computed(() => {
  const pubs = currentCandidate.value.publications || [];
  return sortByYearThenAlphabetically(pubs, "published", "title");
});

const sortedProjects = computed(() => {
  const projects = currentCandidate.value.liProjects || [];
  return sortByYearThenAlphabetically(projects, "startTime", "title");
});

function extractYear(value) {
  if (!value) return 0;
  const match = String(value).match(/\d{4}/);
  return match ? parseInt(match[0]) : 0;
};

function compareAlphabetically(textA, textB) {
  const a = (textA || "").toLowerCase();
  const b = (textB || "").toLowerCase();
  return a.localeCompare(b);
};

function sortByYearThenAlphabetically(items, yearField, textField) {
  return [...items].sort((a, b) => {
    const aYear = extractYear(a[yearField]);
    const bYear = extractYear(b[yearField]);

    if (aYear !== bYear) {
      return bYear - aYear; // Most recent first
    }

    // If same year or no year, sort alphabetically
    return compareAlphabetically(a[textField], b[textField]);
  });
};

function sortAlphabetically(items, textField) {
  return [...items].sort((a, b) => compareAlphabetically(a[textField], b[textField]));
};
</script>

<template>
  <div class="teamable-view">
    <div v-if="showRecommendationsTokens" class="recommendations-tokens-container">
      <RecommendationsTokens
        class="p-16"
        :candidate="currentCandidate"
        allow-hiding
        show-title
      />
    </div>

    <template v-if="!isLoadingCurrentCandidate">
      <AboutMe
        v-if="currentCandidate.aboutMe"
        :candidate="currentCandidate"
        :tokens-enabled="recommendationTokensVisible"
      />

      <h4>Experience</h4>

      <div v-if="currentCandidate.totalEmployment" class="total-experience-container">
        {{ currentCandidate.totalEmployment }}
      </div>

      <div class="experience-container">
        <ul>
          <li v-for="(employment, index) in cleanedUpEmployments" :key="index">
            <Employment
              :index="index"
              :project="currentProject"
              :candidate="currentCandidate"
              :employment="employment"
              :tokens-enabled="recommendationTokensVisible"
              :is-loading-company-info="isLoadingCurrentCandidateCompanies"
            />
            <el-divider class="m-8"></el-divider>

          </li>
        </ul>
      </div>

      <h4>Education</h4>
      <div class="educations-container">
        <div v-for="(education, index) in currentCandidate.educations" :key="index" class="d-flex">
          <Education
            :project="currentProject"
            :candidate="currentCandidate"
            :education="education"
            :tokens-enabled="recommendationTokensVisible"
          />
        </div>
      </div>

      <div id="additional-qualifications-section"></div>

      <template v-if="sortedAwards.length > 0">
        <h4>Awards</h4>
        <div class="awards-container">
          <div v-for="(award, index) in sortedAwards" :key="index">
            <Award
              :project="currentProject"
              :candidate="currentCandidate"
              :award="award"
              :tokens-enabled="recommendationTokensVisible"
            />
          </div>
        </div>
      </template>

      <template v-if="sortedCertifications.length > 0">
        <h4>Certifications</h4>
        <div class="certifications-container">
          <div v-for="(certification, index) in sortedCertifications" :key="index">
            <Certification
              :project="currentProject"
              :candidate="currentCandidate"
              :certification="certification"
              :tokens-enabled="recommendationTokensVisible"
            />
          </div>
        </div>
      </template>

      <template v-if="sortedCourses.length > 0">
        <h4>Courses</h4>
        <div class="courses-container">
          <div v-for="(course, index) in sortedCourses" :key="index">
            <Course
              :project="currentProject"
              :candidate="currentCandidate"
              :course="course"
              :tokens-enabled="recommendationTokensVisible"
            />
          </div>
        </div>
      </template>

      <template v-if="sortedPatents.length > 0">
        <h4>Patents</h4>
        <div class="patents-container">
          <div v-for="(patent, index) in sortedPatents" :key="index">
            <Patent
              :project="currentProject"
              :candidate="currentCandidate"
              :patent="patent"
              :tokens-enabled="recommendationTokensVisible"
            />
          </div>
        </div>
      </template>

      <template v-if="sortedPublications.length > 0">
        <h4>Publications</h4>
        <div class="publications-container">
          <div v-for="(publication, index) in sortedPublications" :key="index">
            <Publication
              :project="currentProject"
              :candidate="currentCandidate"
              :publication="publication"
              :tokens-enabled="recommendationTokensVisible"
            />
          </div>
        </div>
      </template>

      <template v-if="sortedProjects.length > 0">
        <h4>Projects</h4>
        <div class="projects-container">
          <div v-for="(project, index) in sortedProjects" :key="index">
            <Project
              :current-project="currentProject"
              :candidate="currentCandidate"
              :project="project"
              :tokens-enabled="recommendationTokensVisible"
            />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.teamable-view {
  .recommendations-tokens-container {
    background: #FDF9F5;
    border-radius: $tf-global-border-radius;
    margin-bottom: 12px;
  }

  .educations-container,
  .awards-container,
  .certifications-container,
  .courses-container,
  .patents-container,
  .publications-container,
  .projects-container {
    margin-left: 16px;
    margin-bottom: 16px;
  }

  .total-experience-container {
    margin-left: 16px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
  }

  .experience-container {
    margin-left: 16px;

    ul {
      padding-left: 0px;
      list-style: none;

      li {
        margin-bottom: 8px;
      }
    }
  }
}
</style>
